<script setup lang="ts">
import { ElCheckbox } from 'element-plus'

/**
 * One box with one boolean. A set of them driven by a list is `CheckboxGroup`, which
 * takes the options as a prop and models an array — do not `v-for` this one to build it.
 */
defineProps<{
  modelValue?: boolean | string | number
  label?: string | number | boolean
  disabled?: boolean
  border?: boolean
  size?: 'large' | 'default' | 'small'
  indeterminate?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean | string | number): void
  (e: 'change', v: boolean | string | number): void
}>()
</script>

<template>
  <ElCheckbox
    :model-value="modelValue"
    :label="label"
    :disabled="disabled"
    :border="border"
    :size="size"
    :indeterminate="indeterminate"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
    @change="emit('change', $event)"
  >
    <slot />
  </ElCheckbox>
</template>

<style lang="scss" scoped>
/* Element Plus pins a checkbox to one 32px line and forbids the label to wrap, so a
   label longer than its column runs out of its own border. Let it wrap and let the box
   grow — a single-line checkbox measures exactly as before. */
.el-checkbox {
  height: auto;
  min-height: 32px;
  white-space: normal;
}

.el-checkbox.el-checkbox--small {
  min-height: 24px;
}

.el-checkbox.el-checkbox--large {
  min-height: 40px;
}

.el-checkbox.is-bordered {
  padding-top: 4px;
  padding-bottom: 4px;
}

.el-checkbox :deep(.el-checkbox__label) {
  line-height: 1.4;
}
</style>
